import { watch } from 'vue'
import { createGlobalState, useEventListener, useIntervalFn, useThrottleFn } from '@vueuse/core'
import { useUserStore } from '@/stores/user.stores'
import { useAuth } from '@/composables/useAuth'
import { LAST_ACTIVITY_AT } from '@/constants/storageKey.const'

const IDLE_TIMEOUT_MS = 30 * 60 * 1000
const ACTIVITY_THROTTLE_MS = 5000
const CHECK_INTERVAL_MS = 60 * 1000

/**
 * 閒置自動登出（OP#2605 防禦層：token 被偷後就算永久有效，閒置一段時間也會被登出，
 * 縮小可被濫用的時間窗）。
 * @remarks 最後活動時間存在 localStorage（跟 token 同一個 backend），讓多分頁共用同一份，
 *          避免使用者在分頁 A 操作、分頁 B 卻因為沒收到互動事件而被誤判閒置。
 */
export const useIdleLogout = createGlobalState(() => {
  const userStore = useUserStore()
  const { fnLogOut } = useAuth()

  const touchActivity = (): void => {
    if (!userStore.token) return
    localStorage.setItem(LAST_ACTIVITY_AT, String(Date.now()))
  }

  const checkIdle = (): void => {
    if (!userStore.token) return

    const lastActivityAt = Number(localStorage.getItem(LAST_ACTIVITY_AT))
    // 沒有記錄視為未知，不強制登出，避免功能剛上線時對既有登入使用者誤殺
    if (!lastActivityAt) return

    if (Date.now() - lastActivityAt >= IDLE_TIMEOUT_MS) {
      fnLogOut()
    }
  }

  const throttledTouchActivity = useThrottleFn(touchActivity, ACTIVITY_THROTTLE_MS)

  useEventListener(
    window,
    ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'],
    throttledTouchActivity
  )
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    checkIdle()
    touchActivity()
  })

  useIntervalFn(checkIdle, CHECK_INTERVAL_MS)

  // 剛登入（token 從空變有值）視為一次活動，避免沿用登入前殘留的舊時間戳記
  watch(
    () => userStore.token,
    (token, prevToken) => {
      if (token && !prevToken) touchActivity()
    }
  )
})
